// src/components/resources/glossary/GlossaryTermLayout.tsx

'use client'

import { logClientError } from '~/lib/logger-client'
import { motion } from 'motion/react'
import { useState, useMemo, useCallback, useEffect, useRef, useTransition } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import RiBookmarkLine from '~/icons/remix/generated/RiBookmarkLine'
import RiShareLine from '~/icons/remix/generated/RiShareLine'
import { cn } from '~/lib/utils'
import { slugify } from '~/lib/utils/formatters'
import { TabContent } from '~/components/resources/glossary/TabContent'
import { GlossaryLayout } from '~/components/resources/glossary/GlossaryLayout'
import {
  GLOSSARY_SHELL_CONTENT_CONTAINER_CLASSNAME,
  GLOSSARY_SHELL_CONTENT_TOP_GAP_CLASSNAME,
} from '~/components/resources/glossary/glossary-shell'
import { trackTabSwitch, createEngagementTimer, trackResourceLinkClick } from '~/lib/gtag'
import type {
  GlossaryTerm,
  MetricCalculation,
} from '~/lib/resources/types/glossary/terms'
import type { GlossaryCategory } from '~/lib/resources/types/shared/enums'
import type { TabType, TabConfig } from '~/lib/resources/types/glossary/components'
import type { MetricNetworkData } from '~/lib/resources/api/glossary/metrics'
import { getGlossaryHeadingTitle, GLOSSARY_HEADING_TITLE_CLASSNAME } from '~/lib/resources/api/glossary/shared'
import { BenchmarksTable } from '~/components/resources/glossary/BenchmarksTable'
import { HowAdSightsHelpsCard } from '~/components/resources/glossary/HowAdSightsHelpsCard'
import { GlossaryFaqs } from '~/components/resources/glossary/GlossaryFaqs'
import { GlossaryTopicHubs, type TopicHubLink } from '~/components/resources/glossary/GlossaryTopicHubs'

/** localStorage key holding the set of bookmarked glossary term slugs. */
const GLOSSARY_BOOKMARKS_KEY = 'adsights:glossary:bookmarks'

/** Read the set of bookmarked term slugs (empty on any parse/access failure). */
function readBookmarkedSlugs(): Set<string> {
  try {
    const raw = window.localStorage.getItem(GLOSSARY_BOOKMARKS_KEY)
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? new Set(parsed.filter((s): s is string => typeof s === 'string')) : new Set()
  } catch {
    return new Set()
  }
}

interface GlossaryTermLayoutProps {
  term: GlossaryTerm
  /** Server-resolved topic-hub backlinks ({title, slug} only) so this client
   *  component never imports the full RESOURCE_TOPICS registry. */
  topicHubs?: readonly TopicHubLink[]
  relatedTermIndex: Record<string, GlossaryCategory>
  initialTabs: TabConfig[]
  tabContent: Record<TabType, boolean>
  termMetadata: {
    isPlatform: boolean
    platformName: string | null
    hasTags: boolean
    /** Server-resolved canonical slug of the platform-category term for this
     *  metric, or null when none exists. Resolved in the RSC page so the
     *  client bundle doesn't have to ship the alias maps. */
    canonicalPlatformSlug: string | null
  }
  calculationData?: MetricCalculation
  networkData?: MetricNetworkData
}

// Helper function for proper capitalization
const formatTagText = (text: string): string => {
  // Special cases for platform names
  const platformNames: Record<string, string> = {
    'tiktok': 'TikTok',
    'youtube': 'YouTube',
    'facebook': 'Facebook',
    'instagram': 'Instagram',
    'linkedin': 'LinkedIn',
    'twitter': 'Twitter',
    'snapchat': 'Snapchat',
    'pinterest': 'Pinterest'
  }

  // Check if it's a platform name
  const platformName = platformNames[text.toLowerCase()]
  if (platformName) return platformName

  // Otherwise capitalize first letter of each word
  return text.split(/[\s-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function GlossaryTermLayout({
  term,
  topicHubs = [],
  relatedTermIndex,
  initialTabs,
  tabContent,
  termMetadata,
  calculationData,
  networkData
}: GlossaryTermLayoutProps): JSX.Element {
  const [isMounted, setIsMounted] = useState(false)

  // Initialize state with server-computed default tab
  const defaultTab = useMemo(() => initialTabs[0]?.id ?? 'definition', [initialTabs])
  const [activeTab, setActiveTab] = useState<TabType>(defaultTab)
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [, startTabTransition] = useTransition()
  
  // GA4 tracking state
  const tabEngagementTimers = useRef<Map<TabType, () => number>>(new Map())
  const previousTab = useRef<TabType>(defaultTab)

  // Pre-resolved on the server (RSC page) to keep the alias maps out of
  // the client bundle. Read directly from props.
  const canonicalPlatformSlug = termMetadata.canonicalPlatformSlug

  const platformBadgeClasses = cn(
    'inline-flex items-center rounded-full px-3 py-1 text-sm font-medium transition-colors',
    'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-700/10 hover:bg-blue-100 hover:text-blue-800',
    'dark:bg-blue-900/20 dark:text-blue-400 dark:ring-blue-400/20 dark:hover:bg-blue-900/30 dark:hover:text-blue-300'
  )

  const platformLabel = termMetadata.platformName
    ? formatTagText(termMetadata.platformName)
    : null

  const headingTitle = getGlossaryHeadingTitle(term)

  // Mount effect
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot SSR hydration flag, set once after mount (client-only)
    setIsMounted(true)
    // Initialize engagement timer for the default tab
    tabEngagementTimers.current.set(defaultTab, createEngagementTimer())
  }, [defaultTab])

  // Hydrate the persisted bookmark state for this term (client-only).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot hydration of persisted state after mount
    setIsBookmarked(readBookmarkedSlugs().has(term.slug))
  }, [term.slug])

  // Persist the bookmark toggle so it survives navigation/reloads (the button
  // previously toggled local state only, so a "Saved" term was forgotten on
  // the next render/visit).
  const toggleBookmark = useCallback(() => {
    setIsBookmarked(prev => {
      const next = !prev
      try {
        const slugs = readBookmarkedSlugs()
        if (next) slugs.add(term.slug)
        else slugs.delete(term.slug)
        window.localStorage.setItem(GLOSSARY_BOOKMARKS_KEY, JSON.stringify([...slugs]))
      } catch (error) {
        logClientError(error, {
          component: 'GlossaryTermLayout',
          context: 'Failed to persist glossary bookmark'
        })
      }
      return next
    })
  }, [term.slug])

  // Memoize handlers
  const handleTabClick = useCallback((tabId: TabType) => {
    // Dedup against the last *intended* tab (`previousTab.current`, updated
    // synchronously below), NOT the committed `activeTab`: `setActiveTab` runs
    // inside `startTransition`, so on a fast double-tap `activeTab` still lags
    // and a second tap would otherwise pass this guard and record an impossible
    // self-transition (B→B) + reset the engagement timer.
    if (tabId === previousTab.current) return

    // Get engagement time for the previous tab
    const fromTab = previousTab.current
    const engagementTimer = tabEngagementTimers.current.get(fromTab)
    const engagementMs = engagementTimer ? engagementTimer() : undefined

    // Update bookkeeping synchronously so analytics + timers stay accurate.
    previousTab.current = tabId
    tabEngagementTimers.current.set(tabId, createEngagementTimer())

    // Tab switching renders a heavy `TabContent` tree. Wrapping the state
    // change in `startTransition` lets React keep the tap responsive (good
    // for mobile INP) and run the re-render at a lower priority.
    startTabTransition(() => {
      setActiveTab(tabId)
    })

    // Defer analytics off the interaction's critical path so the tap stays
    // under the 200ms INP budget on mobile.
    const trackInIdle = (cb: () => void): void => {
      type IdleWindow = Window & {
        requestIdleCallback?: (cb: () => void) => number
      }
      const w = window as IdleWindow
      if (typeof w.requestIdleCallback === 'function') {
        w.requestIdleCallback(cb)
      } else {
        setTimeout(cb, 0)
      }
    }
    trackInIdle(() => {
      trackTabSwitch({
        resourceType: 'glossary',
        resourceSlug: term.slug,
        fromTab,
        toTab: tabId,
        engagementMs,
        elementId: `glossary-${term.slug}-tabs`
      })
    })
  }, [term.slug, startTabTransition])

  const handleShare = useCallback(async () => {
    if (!navigator.share) return

    try {
      await navigator.share({
        title: term.canonicalTitle ?? term.title,
        text: term.shortDescription,
        url: window.location.href
      })
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        logClientError(error, { component: 'GlossaryTermLayout', context: 'Error sharing' })
      }
    }
  }, [term])

  // Memoize related terms data
  const relatedTermsData = useMemo(() => {
    if (!term.relatedTerms?.length) return []
    
    return term.relatedTerms
      .map(relatedTerm => {
        const category = relatedTermIndex[relatedTerm.slug]
        if (!category) return null
        return {
          ...relatedTerm,
          category,
        }
      })
      .filter((item): item is {
        title: string
        slug: string
        category: GlossaryCategory
        description?: string
        relationship?: 'parent' | 'child' | 'similar' | 'opposite' | 'component' | 'variant' | 'alternative'
      } => Boolean(item))
  }, [term.relatedTerms, relatedTermIndex])

  // The term's canonical title is already in the `term` prop (server data) —
  // threading it into the breadcrumb trail replaces the old client fetch.
  const breadcrumbLeafLabel = term.canonicalTitle ?? term.title

  // Early return for non-mounted state
  if (!isMounted) {
    return (
      <GlossaryLayout showNavigation={false} breadcrumbLeafLabel={breadcrumbLeafLabel}>
        <div className={GLOSSARY_SHELL_CONTENT_CONTAINER_CLASSNAME}>
          <div className="animate-pulse">Loading...</div>
        </div>
      </GlossaryLayout>
    )
  }

  return (
    <GlossaryLayout showNavigation={false} breadcrumbLeafLabel={breadcrumbLeafLabel}>
      <div className={GLOSSARY_SHELL_CONTENT_CONTAINER_CLASSNAME}>
        <div className={`grid lg:grid-cols-3 gap-12 ${GLOSSARY_SHELL_CONTENT_TOP_GAP_CLASSNAME}`}>
          {/* Main Content Area */}
          {/* min-w-0: grid items default to min-width:auto (=min-content), so
              children like the tab nav (whitespace-nowrap tab buttons inside an
              overflow-x-auto wrapper) propagate their wide min-content up to
              the grid track, which expands the column past the viewport on
              mobile instead of letting those wrappers scroll internally.
              min-w-0 caps the track at the viewport so each wrapper's own
              overflow-x-auto actually engages. (BenchmarksTable previously
              had whitespace-nowrap cells with the same problem; cells now
              use break-words so values like `5% / month` wrap inside the
              column instead of overflowing into the adjacent cell — the
              concrete bug reported.) */}
          <motion.div
            className="lg:col-span-2 min-w-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="space-y-8">
              {/* Term Header */}
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  {term.category && (
                    <Link
                      href={`/resources/glossary/${term.category}`}
                      className="group inline-flex items-center rounded-full bg-indigo-50 
                              px-3 py-1 text-sm font-medium text-indigo-700 
                              ring-1 ring-inset ring-indigo-700/10
                              hover:bg-indigo-100 hover:text-indigo-800
                              dark:bg-indigo-900/20 dark:text-indigo-400 
                              dark:ring-indigo-400/20 dark:hover:bg-indigo-900/30 
                              dark:hover:text-indigo-300 transition-colors"
                      prefetch={false}
                    >
                      {formatTagText(term.category)}
                    </Link>
                  )}
                  {termMetadata.isPlatform && platformLabel && (
                    canonicalPlatformSlug ? (
                      <Link
                        href={`/resources/glossary/platform/${canonicalPlatformSlug}`}
                        className={platformBadgeClasses}
                        prefetch={false}
                      >
                        {platformLabel}
                      </Link>
                    ) : (
                      <span className={platformBadgeClasses}>{platformLabel}</span>
                    )
                  )}
                  {termMetadata.hasTags && 'tags' in term && term.tags?.map((tag) => (
                    <Link
                      key={tag}
                      href={`/resources/glossary/tag/${encodeURIComponent(tag)}`}
                      className="group inline-flex items-center rounded-full bg-gray-50
                              px-3 py-1 text-sm font-medium text-gray-700
                              ring-1 ring-inset ring-gray-700/10
                              hover:bg-gray-100 hover:text-gray-800
                              dark:bg-gray-800 dark:text-gray-400
                              dark:ring-gray-400/20 dark:hover:bg-gray-700
                              dark:hover:text-gray-300 transition-colors"
                      prefetch={false}
                    >
                      {formatTagText(tag)}
                    </Link>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <h1
                    data-glossary-speakable-title
                    className={GLOSSARY_HEADING_TITLE_CLASSNAME}
                  >
                    {headingTitle}
                  </h1>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={toggleBookmark}
                      aria-pressed={isBookmarked}
                      className={cn(
                        "flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition-colors",
                        isBookmarked
                          ? "bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400"
                          : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                      )}
                    >
                      <RiBookmarkLine className="h-4 w-4" />
                      {isBookmarked ? 'Saved' : 'Save'}
                    </button>
                    <button
                      onClick={handleShare}
                      className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <RiShareLine className="h-4 w-4" />
                      Share
                    </button>
                  </div>
                </div>
                <p
                  className="mt-4 text-xl text-gray-600 dark:text-gray-400"
                  data-glossary-speakable-summary
                >
                  {term.shortDescription}
                </p>
                {term.images?.hero?.url && (
                  <figure className="mt-8 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
                    <Image
                      src={term.images.hero.url}
                      alt={term.images.hero.alt ?? `${headingTitle} glossary visual`}
                      width={term.images.hero.width ?? 2400}
                      height={term.images.hero.height ?? 1350}
                      className="aspect-[16/9] w-full object-cover"
                      sizes="(min-width: 1024px) 896px, calc(100vw - 2rem)"
                    />
                    <figcaption className="sr-only">
                      Custom glossary image for {headingTitle}.
                    </figcaption>
                  </figure>
                )}
              </div>

              {/* Content Tabs */}
              <div>
                <div className="border-b border-gray-200 dark:border-gray-800">
                  <nav className="-mb-px flex overflow-x-auto" role="tablist">
                    {initialTabs.map((tab) => (
                      <button
                        key={tab.id}
                        id={`tab-${tab.id}`}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        aria-controls={`panel-${tab.id}`}
                        onClick={() => handleTabClick(tab.id)}
                        className={cn(
                          "whitespace-nowrap border-b-2 py-4 px-4 text-sm font-medium flex-shrink-0",
                          activeTab === tab.id
                            ? "border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                        )}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="mt-8" role="tabpanel" id={`panel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
                  <TabContent
                    term={term}
                    activeTab={activeTab}
                    availableTabs={initialTabs.map(tab => tab.id)}
                    tabContent={tabContent}
                    calculationData={calculationData}
                    networkData={networkData}
                  />
                </div>
              </div>

              {/* Enrichment sections — rendered below the tabs so SEO content
                  is always present in the HTML payload regardless of which tab
                  the user has open. Each section self-hides when empty.
                  Field-presence gates (was: `term.category === 'metrics' && …`)
                  so the Q2 2026 audit's Tier-1 enrichment lands on non-metric
                  terms like Creative Fatigue and Incrementality whose
                  formulas + benchmarks are genuinely useful.

                  The TS widening for CreativeTerm + GeneralTerm makes these
                  field accesses safe on those categories; PlatformTerm and
                  ChartTerm don't declare the fields, so we narrow with an
                  `in` operator check that's cheap, doesn't require a cast,
                  and stays correct if a future term type adds the fields.
                  The runtime presence check still gates rendering so
                  legacy non-metric terms without enrichment data don't
                  ship empty wrappers. */}
              {'segmentBenchmarks' in term && term.segmentBenchmarks && term.segmentBenchmarks.length > 0 && (
                <div className="mt-10">
                  <BenchmarksTable
                    benchmarks={term.segmentBenchmarks}
                    metricName={term.canonicalTitle ?? term.title}
                  />
                </div>
              )}

              {'howAdSightsHelps' in term && term.howAdSightsHelps && (
                <div className="mt-10">
                  <HowAdSightsHelpsCard
                    termName={term.canonicalTitle ?? term.title}
                    copy={term.howAdSightsHelps}
                    idPrefix={term.slug}
                    variant={'howAdSightsHelpsVariant' in term ? term.howAdSightsHelpsVariant : undefined}
                  />
                </div>
              )}

              {Array.isArray(term.faqs) && term.faqs.length > 0 && (
                <section
                  aria-labelledby="term-faqs"
                  className="mt-12 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900 sm:p-8"
                >
                  <h2
                    id="term-faqs"
                    className="text-2xl font-semibold text-gray-900 dark:text-white"
                  >
                    Frequently asked questions
                  </h2>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Common questions about {term.canonicalTitle ?? term.title}, answered.
                  </p>
                  <div className="mt-4 sm:mt-6">
                    <GlossaryFaqs
                      idPrefix={`term-faqs-${term.slug}`}
                      items={term.faqs.map((f) => ({
                        question: f.question,
                        answer: typeof f.answer === 'string' ? f.answer : ''
                      }))}
                    />
                  </div>
                </section>
              )}
            </div>
          </motion.div>

          {/* Sidebar */}
          {/* min-w-0: defensive — no current child requires it (Quick Info
              uses flex-wrap badges, Related Terms wraps naturally), but
              matches the main column's constraint so a future overflow-prone
              sidebar child can't silently re-introduce the grid blowout. */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="lg:sticky lg:top-[156px] space-y-6 min-w-0"
          >
            {/* Quick Info Card */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 
                         dark:border-gray-800 dark:bg-gray-900">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Quick Info
              </h3>
              <dl className="space-y-4">
                {/* Category */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Category
                  </dt>
                  <dd className="mt-3">
                    <Link
                      href={`/resources/glossary/${term.category}`}
                      className="inline-flex items-center rounded-full bg-indigo-50 
                              px-2.5 py-0.5 text-sm font-medium text-indigo-700
                              hover:bg-indigo-100 dark:bg-indigo-900/20 
                              dark:text-indigo-400 dark:hover:bg-indigo-900/30"
                    >
                      {formatTagText(term.category)}
                    </Link>
                  </dd>
                </div>

                {/* Platform - if applicable */}
                {termMetadata.isPlatform && termMetadata.platformName && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Platform
                    </dt>
                    <dd className="mt-3">
                      {(() => {
                        const platformSlug =
                          canonicalPlatformSlug ??
                          slugify(termMetadata.platformName, '-')

                        if (!platformSlug) {
                          return (
                            <span
                              className="inline-flex items-center rounded-full bg-indigo-50
                                      px-2.5 py-0.5 text-sm font-medium text-indigo-700
                                      dark:bg-indigo-900/20 dark:text-indigo-400"
                            >
                              {formatTagText(termMetadata.platformName)}
                            </span>
                          )
                        }

                        return (
                          <Link
                            href={`/resources/glossary/platform/${platformSlug}`}
                            className="inline-flex items-center rounded-full bg-indigo-50
                                    px-2.5 py-0.5 text-sm font-medium text-indigo-700
                                    hover:bg-indigo-100 dark:bg-indigo-900/20
                                    dark:text-indigo-400 dark:hover:bg-indigo-900/30"
                            prefetch={false}
                          >
                            {formatTagText(termMetadata.platformName)}
                          </Link>
                        )
                      })()}
                    </dd>
                  </div>
                )}

                {/* Tags - if available */}
                {termMetadata.hasTags && 'tags' in term && term.tags && term.tags.length > 0 && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Tags
                    </dt>
                    <dd className="mt-1 flex flex-wrap gap-2">
                      {term.tags.map((tag) => (
                        <Link
                          key={tag}
                          href={`/resources/glossary/tag/${encodeURIComponent(tag)}`}
                          className="inline-flex items-center rounded-full bg-gray-50 
                                  px-2.5 py-0.5 text-sm font-medium text-gray-600
                                  hover:bg-gray-100 dark:bg-gray-800 
                                  dark:text-gray-400 dark:hover:bg-gray-700"
                        >
                          {formatTagText(tag)}
                        </Link>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Related Terms */}
            {relatedTermsData.length > 0 && (
              <div className="rounded-xl border border-gray-200 bg-white p-6 
                           dark:border-gray-800 dark:bg-gray-900">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Related Terms
                </h3>
                <ul className="space-y-3">
                  {relatedTermsData.map(({ slug, category, title, description }, index) => (
                    <li key={index}>
                      <Link
                        href={`/resources/glossary/${category}/${slug}`}
                        className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 
                               dark:hover:text-indigo-300"
                        onClick={() => {
                          // Track related term clicks from glossary term sidebar
                          // Using direct import (already imported at top) instead of dynamic import
                          trackResourceLinkClick({
                            linkType: 'internal',
                            linkUrl: `/resources/glossary/${category}/${slug}`,
                            linkTitle: title,
                            sourceResource: term.slug,
                            sourceResourceType: 'glossary',
                            linkCategory: 'related_term'
                          })
                        }}
                      >
                        {title}
                      </Link>
                      {description && (
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          {description}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Topic hubs that feature this term — kept on the hydrated path too
                (shared with GlossaryTermServerContent) so the backlink survives
                hydration for JS-enabled visitors, not just crawlers/no-JS. */}
            <GlossaryTopicHubs hubs={topicHubs} variant="card" />
          </motion.div>
        </div>
      </div>
    </GlossaryLayout>
  )
}